import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useSellerLiveInvalidation, useSellerOffers } from '../hooks/queries/sellerQueries';
import {
  getConversationId,
  getOfferAmount,
  getProductId,
  normalizeOffer,
  NormalizedOffer,
} from '../utils/offerNormalize';
import { MessageSquare, CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';

const Offers: React.FC = () => {
  const { t } = useTranslation('bids');
  const { invalidateOffers } = useSellerLiveInvalidation();
  const [actionId, setActionId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');

  const { data: offersData, isLoading } = useSellerOffers({
    page,
    limit: 20,
    status: statusFilter || undefined,
  });

  const offers = offersData?.offers || [];
  const totalPages = offersData?.totalPages || 1;

  const handleAccept = async (offerId: string) => {
    setActionId(offerId);
    try {
      await api.put(`/offers/${offerId}/accept`, {});
      await invalidateOffers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message || t('messages.actionFailed'));
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async (offerId: string) => {
    const reason = window.prompt(t('messages.rejectReasonPrompt'));
    if (reason === null) return;

    setActionId(offerId);
    try {
      await api.put(`/offers/${offerId}/reject`, { reason: reason || undefined });
      await invalidateOffers();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message || t('messages.actionFailed'));
    } finally {
      setActionId(null);
    }
  };

  const getChatNavigationState = (offer: NormalizedOffer) => {
    const conversationId = getConversationId(offer);
    const productId = getProductId(offer);
    if (conversationId) return { conversationId };
    if (productId) return { productId };
    return undefined;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACCEPTED':
      case 'CONVERTED':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'REJECTED':
      case 'EXPIRED':
      case 'CANCELLED':
        return <XCircle className="text-red-500" size={20} />;
      default:
        return <Clock className="text-yellow-500" size={20} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACCEPTED':
      case 'CONVERTED':
        return 'badge-green';
      case 'REJECTED':
      case 'EXPIRED':
      case 'CANCELLED':
        return 'badge-red';
      default:
        return 'badge-yellow';
    }
  };

  const getOfferPercent = (offer: NormalizedOffer) => {
    const listPrice = offer.product?.price;
    const amount = getOfferAmount(offer);
    if (!listPrice || listPrice <= 0) return null;
    return Math.round((amount / listPrice) * 100);
  };

  return (
    <div className="space-y-4 md:space-y-6 max-w-full overflow-x-hidden">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('title')}</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">{t('subtitle')}</p>
        </div>
        <Link
          to="/conversations"
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 self-start"
        >
          <MessageSquare size={16} />
          {t('messages.viewAllMessages')}
        </Link>
      </div>

      <div className="card p-4">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="input-field"
        >
          <option value="">{t('filters.allStatus', { ns: 'common' })}</option>
          <option value="PENDING">{t('status.pending')}</option>
          <option value="ACCEPTED">{t('status.accepted')}</option>
          <option value="REJECTED">{t('status.rejected')}</option>
          <option value="CONVERTED">{t('status.converted')}</option>
          <option value="EXPIRED">{t('status.expired')}</option>
        </select>
      </div>

      {isLoading && !offersData ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-primary" />
        </div>
      ) : offers.length === 0 ? (
        <div className="card p-12 text-center">
          <MessageSquare size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 text-lg">{t('messages.noBidsFound')}</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {offers.map((offer, index) => {
              const offerId = offer.id;
              const isPending = offer.status === 'PENDING';
              const isProcessing = actionId === offerId;
              const chatState = getChatNavigationState(offer);
              const percent = getOfferPercent(offer);

              return (
                <div key={offerId || `offer-${index}`} className="card p-4 md:p-6 overflow-hidden">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                    <div className="flex items-start space-x-3 md:space-x-4 flex-1 min-w-0">
                      {offer.product?.images && offer.product.images.length > 0 && (
                        <img
                          src={offer.product.images[0]}
                          alt={offer.product.name}
                          className="w-16 h-16 md:w-20 md:h-20 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-base md:text-lg text-gray-900 mb-1 truncate">
                          {offer.product?.name}
                        </h3>
                        <p className="text-xs md:text-sm text-gray-500 mb-2">
                          {t('fields.originalPrice')}: ${offer.product?.price}
                          {percent != null && (
                            <span className="ml-2 text-gray-600">
                              · {t('fields.offerVsPrice', { percent })}
                            </span>
                          )}
                        </p>
                        <p className="text-xs md:text-sm text-gray-600 truncate">
                          {t('fields.customer')}:{' '}
                          {offer.buyer
                            ? `${offer.buyer.firstName || ''} ${offer.buyer.lastName || ''}`.trim() ||
                              offer.buyer.email ||
                              t('fields.customer')
                            : t('fields.customer')}
                          {offer.buyer?.email && ` (${offer.buyer.email})`}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-row sm:flex-col items-start sm:items-end justify-between sm:justify-start gap-2 flex-shrink-0">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(offer.status)}
                        <span className={getStatusColor(offer.status)}>
                          {t(`status.${offer.status}`, { defaultValue: offer.status })}
                        </span>
                      </div>
                      <p className="text-xl md:text-2xl font-bold text-custom-primary">
                        ${getOfferAmount(offer)}
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <p className="text-sm text-gray-500">
                      {t('fields.createdAt')}:{' '}
                      {offer.createdAt ? new Date(offer.createdAt).toLocaleString() : '—'}
                      {offer.expiresAt && offer.status === 'ACCEPTED' && (
                        <span className="block text-xs text-amber-600 mt-1">
                          {t('fields.expiresAt')}: {new Date(offer.expiresAt).toLocaleString()}
                        </span>
                      )}
                    </p>

                    <div className="flex flex-wrap gap-2 justify-end">
                      {chatState && (
                        <Link
                          to="/conversations"
                          state={chatState}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <MessageSquare size={16} />
                          {t('messages.viewChat')}
                        </Link>
                      )}
                      {isPending && (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={isProcessing}
                            onClick={() => handleAccept(offerId)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                          >
                            {isProcessing ? (
                              <Loader2 className="animate-spin" size={16} />
                            ) : (
                              <CheckCircle size={16} />
                            )}
                            {t('actions.accept')}
                          </button>
                          <button
                            type="button"
                            disabled={isProcessing}
                            onClick={() => handleReject(offerId)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                          >
                            {isProcessing ? (
                              <Loader2 className="animate-spin" size={16} />
                            ) : (
                              <XCircle size={16} />
                            )}
                            {t('actions.reject')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center space-x-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-gray-200 rounded-lg disabled:opacity-50"
              >
                {t('buttons.previous', { ns: 'common' })}
              </button>
              <span className="px-4 py-2 text-gray-700">
                {t('table.page', { ns: 'common' })} {page} {t('table.of', { ns: 'common' })}{' '}
                {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 bg-gray-200 rounded-lg disabled:opacity-50"
              >
                {t('buttons.next', { ns: 'common' })}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Offers;
